import {Component, DestroyRef, Input, OnChanges, SimpleChanges} from '@angular/core';
import * as _Highcharts from 'highcharts';
import 'highcharts/esm/highcharts-more';
import 'highcharts/esm/modules/boost';
import 'highcharts/esm/modules/exporting';
import 'highcharts/esm/modules/export-data';
import 'highcharts/esm/modules/broken-axis';

import {ChartSettingsGeneratorService} from '@services/charts/line/chart-settings-generator.service';
import {LineChartSettingsParameters} from '@models/charts/line-chart-settings-parameters';
import {TranslationService} from '@services/translation/translation.service';
import {ChartConstructorType} from 'highcharts-angular';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

// Now init modules:


@Component({
    selector: 'scitis-chart-area-range-with-line',
    templateUrl: './area-range-with-line-chart.component.html',
    styleUrls: ['./area-range-with-line-chart.component.scss'],
    standalone: false
})
export class AreaRangeWithLineChartComponent implements OnChanges {
  @Input()
  machineId: string = '';

  @Input()
  chartId: string = '';

  @Input()
  machineSignals: any[] = [];

  @Input()
  chartSettings: any;

  @Input()
  data: any = {};

  @Input()
  stringData: any[] = [];

  @Input()
  exportFileName: string = '';

  @Input()
  exportFileType: string = '';

  highcharts: typeof _Highcharts = _Highcharts;
  chartConstructor: ChartConstructorType = 'chart';
  chartOptions: _Highcharts.Options = {};

  updateFlag: boolean = false;
  oneToOneFlag: boolean = false;
  public showChart: boolean = false;
  private chartInstance: _Highcharts.Chart | undefined;

  public onChartInstance(chart: _Highcharts.Chart): void {
    this.chartInstance = chart;
  }

  constructor(private chartSettingsGeneratorService: ChartSettingsGeneratorService,
              private translationService: TranslationService,
              destroyRef: DestroyRef) {
    this.translationService.translate.onLangChange
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe(() => {
        if (this.showChart) {
          this.refreshChartTranslations();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {

    if (!changes) {
      return;
    }
    this.handleChartDataChange(changes);
    this.handleChartDataExportChange(changes);
  }

  private handleChartDataChange(changes: SimpleChanges) {
    if (changes?.data?.currentValue) {
      if (Object.keys(changes.data.currentValue).length > 0) {
        this.generateChartSettings();
        this.showChart = true;
      } else {
        this.showChart = false;
      }
    }
  }

  private handleChartDataExportChange(changes: SimpleChanges) {
    if (changes?.exportFileName?.currentValue?.length > 0) {
      this.exportChartData(changes.exportFileName.currentValue, this.exportFileType);
    }
  }

  private exportChartData(fileName: string, type: string) {
    const exportFormat = '%Y-%m-%d %H:%M:%S';
    const chart = this.chartInstance ?? this.highcharts.charts[this.highcharts.charts.length - 1];
    if (!chart || !type || type.isNullOrWhitespace()) {
      return;
    }
    // @ts-ignore
    chart.options.exporting.csv.dateFormat = exportFormat;
    // @ts-ignore
    chart.options.exporting.filename = fileName;

    if (type.indexOf('csv') > -1) {
      (chart as any).downloadCSV();
    }
    if (type.indexOf('xls') > -1) {
      (chart as any).downloadXLS();
    }
  }

  private generateChartParameters(): LineChartSettingsParameters {
    const chartParameters: LineChartSettingsParameters = new LineChartSettingsParameters();
    chartParameters.isLineChart = false;
    chartParameters.isInEditMode = false;
    chartParameters.machineId = this.machineId;
    chartParameters.highchartsChart = this.chartInstance ?? this.highcharts.charts[this.highcharts.charts.length - 1];
    chartParameters.machineSignals = this.machineSignals;
    chartParameters.chartSettings = this.chartSettings;
    chartParameters.data = this.data;
    chartParameters.stringData = this.stringData;
    chartParameters.portalLanguage = this.translationService.getActiveLanguage();
    return chartParameters;
  }


  private generateChartSettings() {
    const chartParameters = this.generateChartParameters();
    this.chartOptions = this.chartSettingsGeneratorService.generateChartOptions(chartParameters);
    this.updateFlag = true;
  }

  private refreshChartTranslations(): void {
    if (!this.chartInstance) {
      this.generateChartSettings();
      return;
    }

    const translatedOptions = this.chartSettingsGeneratorService.generateChartOptions(this.generateChartParameters());
    const translatedAxes = Array.isArray(translatedOptions.yAxis) ? translatedOptions.yAxis : [translatedOptions.yAxis];
    const translatedSeries = translatedOptions.series || [];

    this.chartInstance.yAxis.forEach((axis, index) => {
      const options = translatedAxes[index];
      if (options) {
        axis.update(options, false);
      }
    });
    this.chartInstance.series.forEach((series, index) => {
      const name = translatedSeries[index]?.name;
      if (name !== undefined) {
        series.update({name} as _Highcharts.SeriesOptionsType, false);
      }
    });
    this.chartInstance.redraw();
  }


}
